import { getToken } from '../../services/tokenStore';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  Comment as CommentIcon,
  CheckCircle as ApproveIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import LoadingState from '../common/LoadingState';
import { shadow } from '../../theme/tokens';
import { useProject } from '../../context/ProjectContext';
import PolicyDeniedPane from '../access/PolicyDeniedPane';
import { usePolicyStore } from '../../stores/policyStore';
import { usePolicyTabGate } from '../../hooks/policyHooks';

const WorkflowList = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = new URLSearchParams(location.search).get('project');
  const API_BASE_URL = `${(
    process.env.REACT_APP_API_URL ||
    'https://backend-clinical-intake.dizzaroo.com'
  ).replace(/\/$/, '')}/api`;

  // Hub IAM policy gate — only enforced when viewing this as a study tab (?project=).
  // Mirrors StudyFormTabs.jsx's other global tabs (protocol/sap/icf/...): cache-deny →
  // denied card immediately; cache-allow → render, no separate business endpoint to
  // realtime-probe against here, so skipProbe (same as Study Dashboard/Design Studio).
  const { currentProject: policyProject, loadProject } = useProject();
  useEffect(() => {
    if (!projectId) return;
    if (policyProject && String(policyProject._id) === String(projectId)) return;
    loadProject(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);
  const fetchPolicies = usePolicyStore((s) => s.fetchPolicies);
  const policiesLoaded = usePolicyStore((s) => s.loaded);
  const workflowsTabPermitted = usePolicyStore((s) => s.canTab('document_workflows'));
  const policyStudyScopeId =
    policyProject && String(policyProject._id) === String(projectId)
      ? policyProject.iamResourceId || ''
      : '';
  useEffect(() => {
    if (!policyStudyScopeId) return;
    fetchPolicies({ studyId: policyStudyScopeId });
  }, [policyStudyScopeId, fetchPolicies]);
  const workflowsTabGate = usePolicyTabGate({
    active: Boolean(projectId),
    policiesLoaded,
    cachedPermitted: workflowsTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the Document Workflows tab for this study.',
    probeSignature: policyStudyScopeId,
  });

  const getWorkflowProgress = (workflow) => {
    const raw = workflow?.progress;
    if (Number.isFinite(raw)) {
      return Math.max(0, Math.min(100, raw));
    }

    const stateProgress = {
      draft: 10,
      coAuthoring: 35,
      reviewing: 65,
      aiUpdating: 80,
      approved: 100,
      published: 100,
      rejected: 35,
    };

    return stateProgress[workflow?.state] ?? 0;
  };


  const workflowStates = {
    draft: { label: 'Draft', color: 'default', icon: EditIcon },
    coAuthoring: { label: 'Co-Authoring', color: 'primary', icon: GroupIcon },
    reviewing: { label: 'Reviewing', color: 'warning', icon: CommentIcon },
    aiUpdating: { label: 'AI Updating', color: 'info', icon: RefreshIcon },
    approved: { label: 'Approved', color: 'success', icon: ApproveIcon },
    rejected: { label: 'Rejected', color: 'error', icon: ErrorIcon },
    published: { label: 'Published', color: 'success', icon: ApproveIcon }
  };

  useEffect(() => {
    loadWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const url = projectId
        ? `${API_BASE_URL}/workflows?project=${encodeURIComponent(projectId)}`
        : `${API_BASE_URL}/workflows`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch workflows');
      const data = await response.json();
      console.log('Workflows data:', data);
      console.log('First workflow structure:', data[0]);
      console.log('Available fields in first workflow:', Object.keys(data[0] || {}));
      setWorkflows(data);
    } catch (error) {
      console.error('Error loading workflows:', error);
      showSnackbar('Error loading workflows', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredWorkflows = workflows.filter(workflow =>
    (workflow.metadata?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workflow.metadata?.createdBy?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    workflow.state?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleViewWorkflow = (workflowId) => {
    if (!workflowId) {
      showSnackbar('Workflow ID is missing', 'error');
      return;
    }
    navigate(`/documents/workflow/${workflowId}`);
  };

  const handleViewDashboard = (workflowId) => {
    navigate(`/documents/workflow-dashboard/${workflowId}`);
  };

  const handleCreateWorkflow = () => {
    navigate(projectId ? `/documents/workflow/new?project=${encodeURIComponent(projectId)}` : '/documents/workflow/new');
  };

  if (projectId && workflowsTabGate.showDenied) {
    return (
      <Box sx={{ p: 3 }}>
        <PolicyDeniedPane reason={workflowsTabGate.deniedMessage} size="fill" />
      </Box>
    );
  }

  if (loading || (projectId && workflowsTabGate.showSpinner)) {
    return (
      <Box sx={{ p: 3 }}>
        <LoadingState label="Loading workflows…" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Document Workflows"
        subtitle={
          projectId
            ? 'Manage and track document review workflows for this study'
            : 'Manage and track document review workflows'
        }
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateWorkflow}
          >
            Create Workflow
          </Button>
        }
      />

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search workflows..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
      </Box>

      {/* Statistics */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {workflows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Workflows
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {workflows.filter(w => w.state === 'reviewing').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Under Review
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
              {workflows.filter(w => w.state === 'approved').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approved
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'info.main' }}>
              {workflows.filter(w => w.state === 'draft').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Draft
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Workflow List */}
      <Grid container spacing={2.5}>
        {filteredWorkflows.map((workflow) => (
          <Grid item xs={12} md={6} lg={4} key={workflow._id}>
            <Card sx={{ height: '100%', '&:hover': { boxShadow: shadow.md, borderColor: 'text.disabled' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                    {workflow.metadata?.title || 
                     workflow.documentId?.title || 
                     `Workflow ${workflow._id?.slice(-6) || 'Unknown'}`}
                  </Typography>
                  <Chip
                    label={workflowStates[workflow.state]?.label}
                    color={workflowStates[workflow.state]?.color}
                    size="small"
                    icon={workflowStates[workflow.state]?.icon ? React.createElement(workflowStates[workflow.state].icon) : undefined}
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Created by {workflow.metadata?.createdBy?.firstName || 'Unknown'} {workflow.metadata?.createdBy?.lastName || ''} on {workflow.metadata?.createdAt ? new Date(workflow.metadata.createdAt).toLocaleDateString() : 'N/A'}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  {(() => {
                    const progress = getWorkflowProgress(workflow);
                    return (
                      <>
                  <Typography variant="body2" color="text.secondary">
                        Progress: {progress}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                        value={progress}
                    sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                  />
                      </>
                    );
                  })()}
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Co-Authors: {workflow.coAuthors?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reviewers: {workflow.reviewers?.length || 0}
                  </Typography>
                </Box>
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<ViewIcon />}
                  onClick={() => handleViewWorkflow(workflow._id)}
                >
                  Open Workflow
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TimelineIcon />}
                  onClick={() => handleViewDashboard(workflow._id)}
                >
                  Dashboard
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredWorkflows.length === 0 && (
        <Paper variant="outlined">
          <EmptyState
            icon={<FolderIcon fontSize="medium" />}
            title="No workflows found"
            description={searchTerm ? 'Try adjusting your search terms.' : 'Create your first document workflow to get started.'}
          />
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkflowList; 